import { useApolloClient } from "@apollo/client/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BookTextIcon, CheckIcon, PauseIcon, PlayIcon } from "lucide-react";

import { SettlementTreatmentEnum } from "@/__generated__/graphql";
import { HotkeyDecorator } from "@/components/atoms/HotkeyDecorator";
import { Icon } from "@/components/atoms/Icon";
import { Mutations, Queries, Values } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import { useBackendQuery } from "@/hooks/useBackendQuery";
import { useDevice } from "@/hooks/useDevice";
import { State } from "@/state";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Typography } from "@/components/ui/typography";
import { cn } from "@/components/ui/utils";

import {
  buildBookInput,
  journalDocumentsFromSelectionOrFocus,
  useTriageDocumentLineItems,
} from "./TriageJournalEntryPanel";
import {
  useTriageDetailContext,
  useTriageViewActions,
} from "./TriageView.context";
import {
  TriageSettlementChoice,
  type TriageSettlementTreatment,
} from "./TriageSettlement";
import { buildChangelistProposalPresentation } from "./TriageChangelistProposalRow";
import { TriageBudgetAfter } from "./TriageBudgetAfter";
import {
  type TriageBookAs,
  type TriageBookLikeView,
  useTriageBookLike,
} from "./TriageBookLike";
import { TRIAGE_KEYS } from "./TriageView.keys";

/**
 * Every control on triage's two bars - the queue's and the suggestions' - but
 * Book: its icon alone, in a plain square at the bar's height with no edge or
 * shadow, named and keyed by its tooltip, 2px from the others of its kind.
 * Quiet at rest - the tertiary ink - and full ink under the pointer or while
 * on: a toggle pressed (``aria-pressed``) or a menu open or set (``data-on``),
 * on a soft ground besides.
 */
export const TRIAGE_TOOLBAR_BUTTON_CLASS =
  "shrink-0 shadow-none text-muted-foreground aria-pressed:text-foreground data-on:text-foreground aria-pressed:bg-secondary data-on:bg-secondary";

/** Book in the suggestions toolbar: its key close to its end. */
export const TRIAGE_TOOLBAR_BOOK_CLASS =
  "pl-2.5 pr-1 [&_[data-testid=hotkey-decorator]]:ml-2";

/**
 * Hold, on the suggestions' bar beside Preview, before Book: its name and key
 * under the pointer - pressed while the payment is held.
 */
export function TriageHoldButton() {
  const { holdTriage, takeOffHold } = useTriageViewActions();
  const { triage } = useTriageDetailContext();
  const isHeld = Boolean(triage.triageHeldAt);
  const label = isHeld ? "Unhold" : "Hold";

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={label}
          aria-pressed={isHeld}
          className={TRIAGE_TOOLBAR_BUTTON_CLASS}
          onMouseDown={(event) => event.preventDefault()}
          // Held at once, or - held already - let go at once, the payment
          // staying selected. One waiting on the viewer's answer about a receipt
          // is answered in its notice instead.
          onClick={() =>
            isHeld ? takeOffHold?.(triage.id) : holdTriage(triage.id)
          }
          disabled={
            !triage.identifier || (isHeld && triage.triageHeldForViewer)
          }
          data-testid="triage-hold"
          data-held={isHeld}
        >
          <Icon ReactIcon={isHeld ? PlayIcon : PauseIcon} size="sm" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{`${label} (${TRIAGE_KEYS.HOLD.toUpperCase()})`}</TooltipContent>
    </Tooltip>
  );
}

/**
 * Why the payment is held, over what it could be matched to. A receipt a
 * teammate's search found in the viewer's own private mailbox waits on them:
 * they bring it into the business or keep it to themselves, and either way
 * the hold is let go.
 */
export function TriageHoldNotice() {
  const { triage } = useTriageDetailContext();
  const [pendingAnswer, setPendingAnswer] = useState<boolean | null>(null);

  const { mutate: answerDocumentHold, loading } = useBackendMutation({
    mutation: Mutations.ANSWER_DOCUMENT_HOLD,
    refetchQueries: [
      Queries.TRIAGE_DETAIL_QUERY,
      Queries.TRIAGE_CONNECTION_QUERY,
    ],
  });

  if (!triage.triageHoldReason && !triage.triageHeldForViewer) {
    return null;
  }

  const answerHold = (confirm: boolean) => {
    setPendingAnswer(confirm);
    answerDocumentHold({
      variables: {
        input: { transactionIdentifier: triage.identifier, confirm },
      },
      callbacks: {
        onSuccess: () =>
          State.getState().setSnackbarMessage(
            confirm
              ? "Receipt imported. Its match turns up in this payment's suggestions."
              : "Kept private. The payment is no longer on hold.",
          ),
      },
    });
  };

  return (
    <Alert
      data-testid="triage-hold-notice"
      // The panel's own width, edge to edge, as the bar over it is.
      className="rounded-none border-x-0 shrink-0"
    >
      <PauseIcon size="1em" />
      <AlertDescription className="flex flex-wrap items-center justify-between gap-2 text-foreground">
        <span>
          {triage.triageHoldReason ??
            "A receipt for this payment is waiting for you to confirm it."}
        </span>
        {triage.triageHeldForViewer ? (
          <span className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={(loading && pendingAnswer === false) || loading}
              onClick={() => answerHold(false)}
            >
              {loading && pendingAnswer === false && <Spinner />}Keep it private
            </Button>
            <Button
              size="sm"
              disabled={(loading && pendingAnswer === true) || loading}
              onClick={() => answerHold(true)}
            >
              {loading && pendingAnswer === true && <Spinner />}Confirm receipt
            </Button>
          </span>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

/**
 * The entry the payment would book, looked over before it is booked: Preview,
 * beside Book, opens it under their bar (``TriagePreview``) - pressed while it
 * is open, its name and key under the pointer.
 */
export function TriagePreviewButton() {
  const { isPreviewOpen, togglePreview, isOnWayToDocument } =
    useTriageDetailContext();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Preview"
          onMouseDown={(event) => event.preventDefault()}
          onClick={togglePreview}
          // Nothing to look over on a way to its receipt.
          disabled={isOnWayToDocument}
          aria-pressed={isPreviewOpen}
          data-testid="triage-preview"
          className={TRIAGE_TOOLBAR_BUTTON_CLASS}
        >
          <Icon ReactIcon={BookTextIcon} size="sm" />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">{`Preview (${TRIAGE_KEYS.PREVIEW.toUpperCase()})`}</TooltipContent>
    </Tooltip>
  );
}

export function TriageDetailActions() {
  const apolloClient = useApolloClient();
  const { isMobile } = useDevice();
  const { onResolved, registerAccept } = useTriageViewActions();
  const {
    resolved,
    selections,
    setSelection,
    triage,
    isOnWayToDocument,
    journalEntryIsBalanced,
  } = useTriageDetailContext();

  const { mutate: bookLines } = useBackendMutation({
    mutation: Mutations.BOOK,
  });

  const { mutate: bookViaSuggestion } = useBackendMutation({
    mutation: Mutations.BOOK_VIA_SUGGESTION,
  });

  // Booked already - listed among the payments missing a document - there is
  // nothing to book: it is open for its document alone, booked again only by a
  // suggestion on offer picked - the document imported for it.
  const hasNothingToBook =
    triage.isBooked === true &&
    !(
      selections.linesSuggestionIdentifier != null &&
      resolved.linesSuggestion?.status === "PENDING"
    );
  const needsBook = triage.categoryObj != null;
  // A draft in review, opened: nothing to book until it is accepted, and then
  // it is the suggestion it makes that is booked.
  const pickedDraft = selections.draftDocumentIdentifier;

  const selectedChartedAccountIdentifier =
    resolved.chartedAccountOption?.chartedAccount?.identifier ?? null;
  const selectedCategoryIdentifier =
    resolved.categoryOption?.category?.identifier;
  const selectedDocumentIdentifier =
    resolved.documentOptions[resolved.documentOptions.length - 1]?.document
      ?.identifier;
  const hasManualCoding =
    (selectedDocumentIdentifier != null &&
      selections.linesSuggestionIdentifier == null) ||
    selectedChartedAccountIdentifier != null ||
    selectedCategoryIdentifier != null ||
    (resolved.selectedTransactions?.length ?? 0) > 0;

  const journalResolved = useMemo(() => {
    const documents = journalDocumentsFromSelectionOrFocus({
      documentOption: resolved.documentOption,
      documentOptions: resolved.documentOptions,
    });
    return {
      ...resolved,
      documentOption: documents.documentOption,
      documentOptions: documents.documentOptions,
    };
  }, [resolved]);

  const { lineItems: documentLineItems } =
    useTriageDocumentLineItems(journalResolved);

  const willPostJournalEntry =
    needsBook ||
    selectedChartedAccountIdentifier != null ||
    selectedDocumentIdentifier != null ||
    selectedCategoryIdentifier != null ||
    (resolved.selectedTransactions?.length ?? 0) > 0 ||
    selections.linesSuggestionIdentifier != null;

  const canAccept =
    !hasNothingToBook &&
    !isOnWayToDocument &&
    pickedDraft == null &&
    (selections.linesSuggestionIdentifier != null ||
      selectedChartedAccountIdentifier != null ||
      selectedDocumentIdentifier != null ||
      selections.duplicateOptionId != null ||
      needsBook) &&
    (!willPostJournalEntry || journalEntryIsBalanced === true);

  // Another business picked by hand, over what a suggestion says: coded by
  // hand for it, booked from the journal, as a loan or the owner's money.
  const settlementDiffersFromPayer =
    selections.settlementEntityIdentifier != null &&
    selections.settlementEntityIdentifier !== triage.entity?.identifier;
  // A suggestion picked, and nothing changed by hand: it is taken as it is,
  // booking in each business it names.
  const pickedSuggestion =
    selections.linesSuggestionIdentifier != null
      ? resolved.linesSuggestion
      : null;
  // The payment's own tags are put in what is picked as it opens: only one
  // added or taken off is a change by hand. Taken as it is, the payment keeps
  // them.
  const ownTags = new Set(
    (triage.tags ?? []).flatMap((tag) =>
      tag?.identifier ? [tag.identifier] : [],
    ),
  );
  const tagsChanged =
    selections.selectedTags.length !== ownTags.size ||
    selections.selectedTags.some((tag) => !ownTags.has(tag.identifier));
  const acceptsSuggestion =
    pickedSuggestion != null &&
    !hasManualCoding &&
    !settlementDiffersFromPayer &&
    selections.settlementTags.length === 0 &&
    !tagsChanged;
  // What its card says of the payments its documents are paid by already.
  const pickedPresentation = pickedSuggestion
    ? buildChangelistProposalPresentation(pickedSuggestion, triage)
    : null;
  const replacesMatches =
    pickedPresentation != null &&
    pickedPresentation.paidElsewhere.length > 0 &&
    pickedPresentation.canMoveHere &&
    !(
      pickedPresentation.canBothPay &&
      (selections.matchesKeptBySuggestion[pickedSuggestion!.identifier] ?? true)
    );
  // The other payments added on its card to pay the rest of its document,
  // booked with it (``TriageSuggestionContext``).
  const { data: partsData } = useBackendQuery(
    Queries.RULE_SUGGESTION_OTHER_PARTS_QUERY,
    {
      variables: { id: pickedSuggestion?.id ?? "" },
      skip:
        !acceptsSuggestion ||
        (pickedSuggestion?.ruleSuggestionDocumentOptions ?? []).length === 0,
    },
  );
  const parts =
    acceptsSuggestion && partsData?.node?.__typename === "RuleSuggestionObject"
      ? (partsData.node.ruleSuggestionDocumentOptions ?? []).flatMap(
          (option) => option.otherParts ?? [],
        )
      : [];
  const partsBookedWith = parts
    .map((part) => part.transaction)
    .filter((part) =>
      (
        selections.partsBySuggestion[pickedSuggestion!.identifier] ?? []
      ).includes(part.identifier),
    );
  // Paid in full by a payment of a business the person is not in: both cannot
  // pay it, and it is not theirs to move - nothing to book, said on its card.
  const isPaidOutOfReach =
    pickedPresentation != null &&
    pickedPresentation.paidElsewhere.length > 0 &&
    !pickedPresentation.canBothPay &&
    !pickedPresentation.canMoveHere;
  const settlesElsewhere =
    acceptsSuggestion &&
    (pickedSuggestion?.lines ?? []).some(
      (line) => line.entity?.identifier !== triage.entity?.identifier,
    );
  const [settlementConfirmOpen, setSettlementConfirmOpen] = useState(false);
  const bookingCount = 1 + partsBookedWith.length;
  // Similar on, Book does what the bulk list's row picked says to the ticked.
  const detailContext = useTriageDetailContext();
  const isSimilar = detailContext.isSimilar ?? false;
  const bulkBook = detailContext.bulkBook ?? null;
  // What Book is about to book it as: the lines its suggestion books, or the
  // person's own - what its payee's others are booked like (``label_lines``).
  const bookAs = ((): TriageBookAs | null => {
    if (
      hasNothingToBook ||
      !canAccept ||
      isPaidOutOfReach ||
      !triage.identifier
    ) {
      return null;
    }
    if (acceptsSuggestion && pickedSuggestion) {
      const lines = pickedSuggestion.lines ?? [];
      // A line to an account made as it is booked: nothing to copy yet.
      if (
        lines.length === 0 ||
        lines.some((line) => !line.chartedAccount?.identifier)
      ) {
        return null;
      }
      return {
        lines: lines.map((line) => ({
          entityIdentifier:
            line.entity?.identifier ?? triage.entity?.identifier ?? null,
          chartedAccountIdentifier: line.chartedAccount!.identifier,
          amount: String(line.amount ?? 0),
        })),
        links: {
          // One named on its card, not made yet, in place of its own - as
          // its Book files it (``accept_rule_suggestion``).
          categoryIdentifier: selections.suggestionNewCategoryName
            ? null
            : (selections.suggestionCategoryIdentifier ??
              pickedSuggestion.ruleSuggestionChartedAccountOption?.category
                ?.identifier ??
              null),
          // A category its Book makes as it books - named on its card, or the
          // suggestion's own - which the payments like it say they go under.
          newCategoryName:
            selections.suggestionNewCategoryName ??
            pickedSuggestion.newCategoryName ??
            null,
          // The other side of a transfer it books: nothing to copy.
          transactionIdentifiers: (
            pickedSuggestion.ruleSuggestionTransactionOptions ?? []
          ).flatMap((option) =>
            option.transaction?.identifier
              ? [option.transaction.identifier]
              : [],
          ),
        },
        // As said by hand, else as its card recommends - as the server books
        // it, never a way made up here.
        treatment: settlesElsewhere
          ? (selections.settlementTreatment ??
            triage.settlementTreatment ??
            detailContext.recommendedTreatment ??
            null)
          : null,
      };
    }
    const codedByHand =
      hasManualCoding ||
      settlementDiffersFromPayer ||
      selections.settlementTags.length > 0 ||
      selections.linesSuggestionIdentifier != null;
    if (
      !codedByHand &&
      (selections.duplicateOptionId != null ||
        !(
          selectedDocumentIdentifier ||
          selectedChartedAccountIdentifier ||
          needsBook ||
          selectedCategoryIdentifier
        ))
    ) {
      return null;
    }
    const input = buildBookInput({
      transactionIdentifier: triage.identifier,
      triage,
      resolved: journalResolved,
      documentLineItems,
      settlementEntityIdentifier:
        detailContext.journalSettlement?.settlementEntityIdentifier ?? null,
      newCategoryName: selections.newCategoryName,
    });
    return { lines: input.lines, links: input.links };
  })();
  // Its payee's others, as the payment opens - ticked the same in the queue
  // as Similar shows them there.
  const keptBookLikeView = useState<TriageBookLikeView | null>(null);
  const bookLikePayment = triage.identifier
    ? {
        id: triage.id,
        identifier: triage.identifier,
        entityIdentifier: triage.entity?.identifier,
        amount: triage.amount,
      }
    : null;
  // In Similar, what they are about to be booked like is the post picked in
  // its bulk list, there alone: none picked there, none - whatever the payment
  // had picked before (``bulkPick``).
  const bulkPick = detailContext.bulkPick ?? null;
  const bookAsLike =
    isSimilar && (bulkPick == null || bulkPick.startsWith("move:"))
      ? null
      : bookAs;
  // Asked once, with their documents, as the payment opens: Similar reads
  // this answer.
  const bookLike = useTriageBookLike(
    bookLikePayment,
    bookAsLike,
    keptBookLikeView,
    true,
    detailContext.queueFilter ?? null,
  );
  // Similar turned on: the keys on the payment itself, as they were in the
  // queue - not on whichever they were left on the last time. (Off, they are
  // let go once Similar's list has gone: ``TriageDetail``.)
  const [wasSimilar, setWasSimilar] = useState(isSimilar);
  if (isSimilar !== wasSimilar) {
    setWasSimilar(isSimilar);
    if (isSimilar) bookLike.setKeyed(triage.id);
  }
  // As many as its card in the queue says after its payee - the one field
  // both read, so the two cannot differ: Similar is not waited on.
  const othersCount = triage.payeePaymentCount ?? 0;
  // What the toolbar's Similar says of them, as it changes.
  const setBookLikeOffer = detailContext.setBookLikeOffer;
  const isBookLikeDisabled = othersCount === 0;
  const isCountLoading = false;
  const offeredCount = triage.identifier ? othersCount : null;
  // And the list itself, ticked as it was left: its Move moves those ticked,
  // and Similar's bulk list books them like it - as it is about to be booked,
  // by its own Book after them.
  const bookAsKey = JSON.stringify(bookAsLike);
  const bookAsKept = useMemo(
    () => JSON.parse(bookAsKey) as TriageBookAs | null,
    [bookAsKey],
  );
  const canBookItself = canAccept && !isPaidOutOfReach;
  const onBook = useCallback(() => bookRef.current(), []);
  useEffect(() => {
    setBookLikeOffer?.({
      count: offeredCount,
      isLoading: isCountLoading,
      isDisabled: isBookLikeDisabled,
      bookLike,
      bookAs: bookAsKept,
      canBookItself,
      onBook,
    });
  }, [
    setBookLikeOffer,
    offeredCount,
    isCountLoading,
    isBookLikeDisabled,
    bookLike,
    bookAsKept,
    canBookItself,
    onBook,
  ]);
  useEffect(() => () => setBookLikeOffer?.(null), [setBookLikeOffer]);
  const beneficiaryName =
    State.getState().entities.find(
      (entity) =>
        entity.identifier ===
        (settlementDiffersFromPayer
          ? selections.settlementEntityIdentifier
          : pickedSuggestion?.lines?.find(
              (line) => line.entity?.identifier !== triage.entity?.identifier,
            )?.entity?.identifier),
    )?.name ?? "another business";
  const payerName = triage.entity?.name ?? "This business";
  // Coded by hand, it is a loan or the owner's money: nothing of the other's
  // is known well enough to call it the payer's own.
  const promptTreatments: TriageSettlementTreatment[] = [
    {
      __typename: "SettlementTreatmentObject",
      treatment: SettlementTreatmentEnum.Loan,
      title: "Loan",
      description: `${beneficiaryName} pays ${payerName} back. ${payerName} records what ${beneficiaryName} owes it, and ${beneficiaryName} what it owes ${payerName}.`,
      isLoan: true,
      isRecommended: false,
      lines: [],
    },
    {
      __typename: "SettlementTreatmentObject",
      treatment: SettlementTreatmentEnum.OwnerEquity,
      title: "Owner's money",
      description: `Nobody pays it back: the owner moved the money from ${payerName} to ${beneficiaryName}. ${payerName} records it as money its owner took out, and ${beneficiaryName} as money its owner put in.`,
      isLoan: false,
      isRecommended: true,
      lines: [],
    },
  ];

  const finishAccept = (alsoRemove?: string[]) => {
    onResolved(
      alsoRemove && alsoRemove.length > 0
        ? { alsoRemoveTransactionIdentifiers: alsoRemove }
        : undefined,
    );
  };

  // Booked at once, as Book is pressed: off the queue and on to the next, the
  // server's answer not waited on. Refused, the queue is read again - the
  // payment back on it - and the server's words said.
  const refetchTriageListOnBookError = () => {
    void apolloClient.refetchQueries({
      include: [Queries.TRIAGE_CONNECTION_QUERY],
    });
  };

  // Refused, the queue is read again - the payment back on it.
  const bookCallbacks = () => ({ onError: refetchTriageListOnBookError });

  const bookManualJournal = (
    alsoRemoveList: string[],
    treatment?: SettlementTreatmentEnum | null,
  ) => {
    if (!triage.identifier) {
      return;
    }
    // The business it settles in: as coded by hand, else the one its Move said
    // it is for - settled as asked, else as its Move said.
    const settlement = detailContext.journalSettlement ?? null;
    const settlementEntityIdentifier =
      settlement?.settlementEntityIdentifier ?? null;
    const settledAs =
      treatment ??
      (selections.settlementEntityIdentifier == null
        ? (triage.settlementTreatment ?? null)
        : null);
    const payerTags =
      selections.selectedTags.length > 0
        ? selections.selectedTags
        : (triage.tags ?? [])
            .filter((tag) => tag?.identifier)
            .map((tag) => ({
              identifier: tag!.identifier!,
            }));
    bookLines({
      variables: {
        input: {
          ...(settledAs && settlementEntityIdentifier
            ? { treatment: settledAs }
            : {}),
          // The card changed, its answer to whether both paid its documents
          // or this one takes their matches is kept all the same.
          ...(replacesMatches ? { replacesMatches: true } : {}),
          ...buildBookInput({
            transactionIdentifier: triage.identifier,
            triage,
            resolved: journalResolved,
            documentLineItems,
            settlementEntityIdentifier,
            payerTagIdentifiers: payerTags
              .map((tag) => tag.identifier)
              .filter((identifier): identifier is string =>
                Boolean(identifier),
              ),
            settlementTagIdentifiers: selections.settlementTags.map(
              (tag) => tag.identifier,
            ),
            // Named on the account picked by hand: made as it is booked.
            newCategoryName: selections.newCategoryName,
            settlement,
          }),
        },
      },
      callbacks: bookCallbacks(),
    });
    finishAccept(alsoRemoveList);
  };

  const runAcceptFlow = (treatment?: SettlementTreatmentEnum | null) => {
    const alsoRemove = new Set<string>();
    for (const transaction of resolved.selectedTransactions ?? []) {
      if (transaction.identifier) {
        alsoRemove.add(transaction.identifier);
      }
    }
    if (selections.linesSuggestionIdentifier && resolved.linesSuggestion) {
      const triageAmount = Number(triage.amount ?? 0);
      const lineAmounts = new Set(
        (resolved.linesSuggestion.lines ?? [])
          .map((line) => Number(line.amount ?? 0))
          .filter((amount) => Number.isFinite(amount) && amount !== 0),
      );
      for (const option of resolved.linesSuggestion
        .ruleSuggestionTransactionOptions ?? []) {
        const peer = option.transaction;
        if (
          peer?.identifier &&
          peer.amount != null &&
          Number(peer.amount) !== triageAmount &&
          lineAmounts.has(Number(peer.amount))
        ) {
          alsoRemove.add(peer.identifier);
        }
      }
    }
    // The other parts booked with it leave the queue with it.
    for (const part of partsBookedWith) {
      if (!part.journalEntry) {
        alsoRemove.add(part.identifier);
      }
    }
    if (triage.identifier) {
      alsoRemove.delete(triage.identifier);
    }
    const alsoRemoveList = [...alsoRemove];
    if (acceptsSuggestion && pickedSuggestion) {
      // Its entries are the server's to book - in each business it names, as
      // the person said the two settle it.
      bookViaSuggestion({
        variables: {
          input: {
            suggestionIdentifier: pickedSuggestion.identifier,
            ...(treatment ? { treatment } : {}),
            // A category changed on its card, booked in place of its own.
            ...(selections.suggestionCategoryIdentifier
              ? { categoryIdentifier: selections.suggestionCategoryIdentifier }
              : {}),
            // One named on its card, under an account made as it is booked.
            ...(selections.suggestionNewCategoryName
              ? { newCategoryName: selections.suggestionNewCategoryName }
              : {}),
            // Its documents' matches with other payments, moved here unless
            // both may pay them and its card leaves them (as its card says).
            ...(replacesMatches ? { replacesMatches: true } : {}),
            // The other parts of what pays its document, as its card ticks them.
            ...(partsBookedWith.length > 0
              ? { parts: partsBookedWith.map((part) => part.identifier) }
              : {}),
          },
        },
        callbacks: bookCallbacks(),
      });
      finishAccept(alsoRemoveList);
      return;
    }

    if (
      hasManualCoding ||
      settlementDiffersFromPayer ||
      selections.settlementTags.length > 0 ||
      selections.linesSuggestionIdentifier
    ) {
      bookManualJournal(alsoRemoveList, treatment);
      return;
    }

    if (selections.duplicateOptionId && resolved.duplicateSuggestion) {
      bookViaSuggestion({
        variables: {
          input: {
            suggestionIdentifier: resolved.duplicateSuggestion.identifier,
          },
        },
        callbacks: bookCallbacks(),
      });
      finishAccept(alsoRemoveList);
      return;
    }

    if (
      selectedDocumentIdentifier ||
      selectedChartedAccountIdentifier ||
      needsBook ||
      selectedCategoryIdentifier
    ) {
      bookManualJournal(alsoRemoveList, null);
      return;
    }

    finishAccept(alsoRemoveList);
  };

  // Booked: the payment open alone - or, Similar on, what the bulk list's row
  // picked does to the ticked.
  const book = (alone = false) => {
    if (isSimilar && !alone) {
      if (bulkBook && !bulkBook.isDisabled) bulkBook.run();
      return;
    }
    if (hasNothingToBook) return;
    if (!canAccept) {
      State.getState().pushToCommandStack({
        commandDefinitionId: "search",
      });
      return;
    }
    // A suggestion for another business is taken as its card was answered;
    // one coded by hand is asked about first.
    if (settlementDiffersFromPayer) {
      setSelection("settlementTreatment", null);
      setSettlementConfirmOpen(true);
      return;
    }
    // A suggestion for another business settles as picked on its card - or,
    // none picked, as the card recommends (as its Move said, as the payments
    // it was learned from were, or a loan): the server's to say.
    runAcceptFlow(
      settlesElsewhere
        ? (selections.settlementTreatment ?? triage.settlementTreatment ?? null)
        : null,
    );
  };

  // Its own Book, the payment alone - what the bulk list books after the
  // ticked.
  const bookRef = useRef(() => book(true));
  bookRef.current = () => book(true);
  const pressRef = useRef(() => book());
  pressRef.current = () => book();

  useEffect(() => {
    registerAccept(() => pressRef.current());
    return () => registerAccept(null);
  }, [registerAccept]);

  const holdAndPreview = (
    <div className="flex items-center gap-0.5">
      <TriageHoldButton />
      {!hasNothingToBook && <TriagePreviewButton />}
    </div>
  );

  return (
    <div
      className="flex items-center gap-3 shrink-0"
      data-testid="triage-detail-actions"
      onClick={(event) => event.stopPropagation()}
    >
      {/* Book, at the bar's far end - the one to press once there is
          something to book it by, Hold and Preview just before it. Similar
          on, what the bulk list's row picked does to the ticked; booked, it
          says so in its place. */}
      {isSimilar ? (
        <>
          {holdAndPreview}
          <Button
            // At work, its spinner in its tick's place, its words still said:
            // the small one.
            size="sm"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => book()}
            disabled={
              (bulkBook?.isMaking ?? false) || !bulkBook || bulkBook.isDisabled
            }
            variant={bulkBook && !bulkBook.isDisabled ? "default" : "outline"}
            data-testid="triage-book"
            className={TRIAGE_TOOLBAR_BOOK_CLASS}
          >
            {(bulkBook?.isMaking ?? false) ? (
              <Spinner />
            ) : (
              <CheckIcon size="1em" />
            )}
            {bulkBook?.label ?? "Book"}
            {bulkBook && !bulkBook.isDisabled ? (
              <HotkeyDecorator
                hotkey={TRIAGE_KEYS.ACCEPT_META}
                color="inherit"
              />
            ) : undefined}
          </Button>
        </>
      ) : hasNothingToBook ? (
        <>
          {holdAndPreview}
          <Typography
            level="body-sm"
            data-testid="triage-booked"
            className="text-muted-foreground/80 pl-2 pr-2 whitespace-nowrap"
          >
            {triage.belongsToEntity
              ? `Booked, moved to ${triage.belongsToEntity.name}`
              : "Booked"}
          </Typography>
        </>
      ) : (
        <>
          {/* What it leaves on the budget's lines it lands on, as it is about to be booked. */}
          {Values.IS_BUDGET_SHOWN && !isMobile && bookAs != null && (
            <TriageBudgetAfter
              entityIdentifier={triage.entity?.identifier}
              amountCurrency={triage.amountCurrency}
              lines={(bookAs.lines ?? []).flatMap((line) =>
                line.entityIdentifier === triage.entity?.identifier &&
                line.chartedAccountIdentifier
                  ? [
                      {
                        chartedAccountIdentifier: line.chartedAccountIdentifier,
                        amount: String(line.amount),
                      },
                    ]
                  : [],
              )}
            />
          )}
          {holdAndPreview}
          <Button
            size="sm"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => book()}
            disabled={!canAccept || isPaidOutOfReach}
            // What it books by, the one to press: solid once there is one.
            variant={canAccept ? "default" : "outline"}
            data-testid="triage-book"
            className={TRIAGE_TOOLBAR_BOOK_CLASS}
          >
            <CheckIcon size="1em" />
            {bookingCount > 1 ? `Book ${bookingCount} payments` : "Book"}
            {canAccept ? (
              <HotkeyDecorator
                hotkey={TRIAGE_KEYS.ACCEPT_META}
                color="inherit"
              />
            ) : undefined}
          </Button>
        </>
      )}

      <Dialog
        open={settlementConfirmOpen}
        onOpenChange={(open) => !open && setSettlementConfirmOpen(false)}
      >
        <DialogContent
          className="sm:max-w-160"
          data-testid="triage-settlement-prompt"
        >
          <DialogHeader>
            <DialogTitle>Was it a loan or an expense?</DialogTitle>
            <DialogDescription>
              {Number(triage.amount ?? 0) < 0
                ? `${triage.entity?.name ?? "This business"} paid it for ${beneficiaryName}.`
                : `${triage.entity?.name ?? "This business"} took it in for ${beneficiaryName}.`}
            </DialogDescription>
          </DialogHeader>
          <TriageSettlementChoice
            treatments={promptTreatments}
            treatment={selections.settlementTreatment}
            onChange={(treatment) =>
              setSelection("settlementTreatment", treatment)
            }
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSettlementConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={selections.settlementTreatment == null}
              data-testid="triage-settlement-accept"
              onClick={() => {
                setSettlementConfirmOpen(false);
                runAcceptFlow(selections.settlementTreatment);
              }}
            >
              Accept
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
